import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState, useRef, useEffect } from 'react'
import axios from 'axios'
import Button from '@/components/Button'
import logo from '@/assets/logo.png'

export const Route = createFileRoute('/join')({
  component: JoinPage,
})

function JoinPage() {
  const navigate = useNavigate()
  const [code, setCode] = useState(['', '', ''])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const inputRefs = [
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
  ]

  // Auto-focus the first input
  useEffect(() => {
    inputRefs[0].current?.focus()
  }, [])

  const handleChange = (index: number, value: string) => {
    const upper = value.toUpperCase().replace(/[^A-Z0-9]/g, '')
    const newCode = [...code]
    newCode[index] = upper
    setCode(newCode)

  }

  const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key === '-' || e.key === 'Tab') {
      if (e.key === '-') e.preventDefault()
      if (index < 2) {
        inputRefs[index + 1].current?.focus()
      }
    }
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs[index - 1].current?.focus()
    }
  }

  const handlePaste = (e: React.ClipboardEvent) => {
    const paste = e.clipboardData.getData('text').trim().toUpperCase()
    // Try to parse a full code like "WARM-PANDA-42"
    const parts = paste.split('-')
    if (parts.length === 3) {
      e.preventDefault()
      setCode(parts)
      inputRefs[2].current?.focus()
    }
  }

  const fullCode = code.join('-')
  const isCodeComplete = code[0].length > 0 && code[1].length > 0 && code[2].length > 0

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault()
    if (!isCodeComplete || isLoading) return
    setIsLoading(true)
    setError('')
    try {
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}/public/invitations/lookup/${encodeURIComponent(fullCode)}`
      )
      const { invitationCode, status, clerkTicket } = response.data
      if (status !== 'PENDING') {
        setError('This invite code has expired or already been used. Please ask your family member to send a new one.')
        return
      }
      // If we have a Clerk ticket, use it for auto-authentication (skips email verification)
      // Otherwise fall back to the normal signup flow
      navigate({
        to: '/signup',
        search: {
          invitationcode: invitationCode,
          ...(clerkTicket ? { __clerk_ticket: clerkTicket } : {}),
        },
      })
    } catch {
      setError("We couldn't find that code. Check the spelling and try again.")
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="relative min-h-screen bg-white">
      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-4">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="flex justify-center mb-10">
            <img src={logo} alt="GranGuard" className="w-36" />
          </div>

          {/* Headline */}
          <div className="text-center mb-8">
            <h1
              className="font-medium text-gray-900 leading-[1.05] tracking-[-0.03em] mb-3"
              style={{ fontSize: 'clamp(1.6rem, 4vw, 2.2rem)' }}
            >
              Enter your invite code
            </h1>
            <p className="text-base text-gray-500 leading-relaxed">
              Someone in your family invited you to GranGuard.
              <br />
              Type in the code from your email to get started.
            </p>
          </div>

          {/* Error */}
          {error && (
            <div className="mb-5 px-4 py-3 rounded-xl bg-red-50 border border-red-100 text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Code input */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="flex items-center justify-center gap-2">
              {[0, 1, 2].map((index) => (
                <div key={index} className="flex items-center gap-2">
                  <input
                    ref={inputRefs[index]}
                    type="text"
                    className="w-28 sm:w-32 h-14 text-center text-lg font-bold text-gray-900 uppercase tracking-wider bg-gray-50 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100 transition placeholder:text-gray-300 placeholder:font-normal placeholder:tracking-normal placeholder:text-base placeholder:normal-case"
                    value={code[index]}
                    onChange={(e) => handleChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(e, index)}
                    onPaste={handlePaste}
                    placeholder={index === 0 ? 'WORD' : index === 1 ? 'WORD' : 'NN'}
                    autoComplete="off"
                    spellCheck={false}
                  />
                  {index < 2 && (
                    <span className="text-2xl font-bold text-gray-300 select-none">-</span>
                  )}
                </div>
              ))}
            </div>

            <Button
              type="submit"
              variant="primary"
              className="text-base py-3.5 font-semibold rounded-lg"
              fullWidth
              isLoading={isLoading}
              disabled={!isCodeComplete || isLoading}
            >
              {isLoading ? 'Checking...' : 'Continue'}
            </Button>
          </form>

          {/* Help text */}
          <div className="mt-8 text-center space-y-3">
            <p className="text-sm text-gray-400">
              Your code looks like <span className="font-mono font-medium text-gray-500">WARM-PANDA-42</span> and was in the email from GranGuard.
            </p>
            <p className="text-sm text-gray-400">
              Already have an account?{' '}
              <a href="/signin" className="text-violet-600 hover:text-violet-700 font-medium underline underline-offset-2">
                Sign in
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
